// frontend/src/components/themed/ThemedSelect.js - Professional themed select component
import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { ChevronDown } from 'lucide-react';

const ThemedSelect = forwardRef(({ 
  label,
  options = [],
  error,
  helperText,
  disabled = false,
  fullWidth = true,
  size = 'md',
  placeholder,
  className = '',
  containerClassName = '',
  ...props 
}, ref) => {
  const { t } = useTranslation();
  const { components, text, states, cn } = useTheme();

  // Only an omitted prop uses the shared default; explicit empty or null values
  // must continue to suppress the placeholder for callers that do not want one.
  const resolvedPlaceholder = placeholder === undefined
    ? t('selectAnOption')
    : placeholder;

  // Size classes
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm pr-8',
    md: 'px-3 py-2 pr-10',
    lg: 'px-4 py-3 text-lg pr-12',
  };

  // Build select classes
  const selectClasses = cn(
    'block rounded-md appearance-none cursor-pointer',
    fullWidth ? 'w-full' : '',
    sizeClasses[size],
    disabled ? components.input.disabled : components.input.base,
    error ? components.input.error : '',
    !disabled && !error ? components.input.focus : '',
    states.focus.outline,
    className
  );

  return (
    <div className={cn(fullWidth ? 'w-full' : '', containerClassName)}>
      {label && (
        <label className={`block text-sm font-medium mb-1 ${text.secondary}`}>
          {label}
        </label>
      )}
      
      <div className="relative">
        <select
          ref={ref}
          disabled={disabled}
          className={selectClasses}
          {...props}
        >
          {resolvedPlaceholder && (
            <option value="" disabled>
              {resolvedPlaceholder}
            </option>
          )}
          {/* Support consumers that pass <option> children directly */}
          {props.children}
          {options.map((option) => (
            <option 
              key={option.value} 
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>
        
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <ChevronDown className={`h-5 w-5 ${text.muted}`} />
        </div>
      </div>
      
      {(error || helperText) && (
        <p className={`mt-1 text-sm ${error ? 'text-red-600' : text.muted}`}>
          {error || helperText}
        </p>
      )}
    </div>
  );
});

ThemedSelect.displayName = 'ThemedSelect';

export default ThemedSelect;
